/**
 * Fetch policy for the Channel Console (A7Y-3776).
 *
 * Its own module so a test can assert it without importing the page, its
 * charts and the API client — the same reason `audit-spend/fetch-policy.ts`
 * is separate, and the same failure it guards against.
 *
 * Why there is no polling, and why it matters MORE here than on Audit Spend:
 * every uncached day on this page is a raw Mixpanel `/export` call returning a
 * full day of events (measured 2026-09-11: 33,016 events, ~15 MB for one day).
 * Mixpanel's export endpoint is rate-limited per project and shared with the
 * nightly engagement-scoring job in `a7y-functions`. A polling dashboard left
 * open on a second monitor would not merely cost money — it would contend for
 * the same rate limit that job depends on.
 *
 * A7Y-3777 moved serving off the per-day KV cache and onto the
 * `channel_console_daily_history` BigQuery table, written by the nightly
 * pull. That changes where the numbers come from, not this policy: the window
 * is still thirty closed days, still cannot change within the hour, and a
 * poll would still contend for the same Mixpanel rate limit through the
 * Refresh path.
 */

/**
 * How long a payload is treated as current.
 *
 * An hour. The window itself is thirty CLOSED days ending yesterday,
 * so nothing in it can change within the hour — what the short bound buys is
 * that a page left open overnight rolls onto the new day's window instead of
 * showing yesterday's under a fresh timestamp, which is exactly what the
 * `generated_at` stamp exists to prevent.
 */
export const STALE_TIME_MS = 60 * 60_000;

/**
 * The options every Channel Console query is built from. Guarded by
 * `fetch-policy.test.ts`: re-introducing an interval here is a one-word change
 * that breaks nothing, reviews clean, and surfaces only as Mixpanel rate-limit
 * contention with a nightly job — so a test reports it rather than a person.
 */
export const CHANNEL_CONSOLE_FETCH_POLICY = {
  refetchInterval: false,
  refetchOnWindowFocus: true,
  staleTime: STALE_TIME_MS,
} as const;
